/** The pocket's History level (spec §7.2 "History ›"): when a fact was learned, changed and given to tasks, newest first. */
import { useEffect, useState } from "react";
import { getNodeActivity, type MemoryNodeActivity } from "@/api/memory";
import {
  Icons,
  PocketRow,
  PocketSection,
  PocketStatement,
  Spinner,
} from "@/components/ui";
import { formatRelativeTime, useT, type Translator } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import type en from "@/i18n/messages/frames-memory/en";
import { capitalize } from "@/lib/memory-thing-kinds";
import { useProjectStore } from "@/stores/project-store";
type OperationKey =
  | "operationLearned"
  | "operationChanged"
  | "operationConfirmed"
  | "operationRejected";
type ActorKey = "actorYou" | "actorDenker" | "actorCurated";
const OPERATION_KEY: Readonly<Record<string, OperationKey>> = {
  create: "operationLearned",
  replace: "operationChanged",
  update: "operationChanged",
  accept: "operationConfirmed",
  reject: "operationRejected",
};
const ACTOR_KEY: Readonly<Record<string, ActorKey>> = {
  user: "actorYou",
  agent: "actorDenker",
  system: "actorDenker",
  curator: "actorCurated",
};
function rows(
  activity: MemoryNodeActivity,
  ticketPrefix: string,
  t: Translator<typeof en>,
): Array<{ at: string; title: string }> {
  const word = (key: OperationKey | ActorKey | undefined, fallback: string) =>
    key ? t(key) : fallback;
  return [
    ...activity.deliveries.map((delivery) => {
      const title = delivery.task_title ?? t("untitledTask");
      return {
        at: delivery.delivered_at,
        title: t("historyGivenTo", {
          task:
            delivery.task_number !== null
              ? `${ticketPrefix}-${delivery.task_number} ${title}`
              : title,
        }),
      };
    }),
    ...activity.revisions.map((revision) => ({
      at: revision.created_at,
      title: t("historyRevision", {
        operation: word(
          OPERATION_KEY[revision.operation],
          capitalize(revision.operation),
        ),
        actor: word(ACTOR_KEY[revision.actor_kind], revision.actor_kind),
      }),
    })),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}
export function PocketHistoryLevel({ itemId }: { itemId: string }) {
  const [activity, setActivity] = useState<{
    id: string;
    data: MemoryNodeActivity;
  } | null>(null);
  const t = useT(framesMemoryMessages);
  const ticketPrefix = useProjectStore(
    (s) => s.currentProject?.ticket_prefix ?? "T",
  );
  useEffect(() => {
    let active = true;
    const done = (data: MemoryNodeActivity) => {
      if (active) setActivity({ id: itemId, data });
    };
    getNodeActivity(itemId).then(done, () =>
      done({ deliveries: [], revisions: [] }),
    );
    return () => {
      active = false;
    };
  }, [itemId]);
  if (activity?.id !== itemId)
    return (
      <div className="flex justify-center py-4">
        <Spinner className="h-4 w-4 text-muted" />
      </div>
    );
  const list = rows(activity.data, ticketPrefix, t);
  if (list.length === 0)
    return (
      <PocketStatement
        size="empty"
        title={t("historyEmpty")}
        testId="memory-history-empty"
      />
    );
  return (
    <PocketSection testId="memory-history">
      {list.map((row, index) => (
        <PocketRow
          key={`${row.at}-${index}`}
          testId={`memory-history-row-${index}`}
          leading={<Icons.Clock className="h-4 w-4" aria-hidden="true" />}
          title={row.title}
          trailing={formatRelativeTime(row.at, { style: "narrow" })}
        />
      ))}
    </PocketSection>
  );
}
